import { useSelector } from "@xstate/react";
import { type Actor, assign, createActor, setup } from "xstate";

import { PatientSearchRequest } from "@elation/api-client/src/stitch-api/models/patientSearchRequest";
import { Patient } from "@elation/api-client/src/stitch-web";

import { IS_IN_IFRAME } from "@src/env";

export type Ehr = "elation" | "athenahealth" | "eclinicalworks";

export type PatientFromIframe = {
  id: number;
} & PatientSearchRequest;

interface IframeContext {
  ehr: Ehr | null;
  patient: PatientFromIframe | null;
  userEmail: string | null;
  stitchPatient: Patient | null;
  error: string | null;
  initialized: boolean;
  authenticated: boolean;
}

export type IframeEvent =
  | {
      type: "STORE_INITIALIZED";
      ehr: Ehr | null;
      patient: PatientFromIframe | null;
      userEmail: string | null;
    }
  | { type: "SESSION_READY" }
  | { type: "SESSION_MISSING" }
  | { type: "SYNC_SUCCESS"; patient: Patient }
  | { type: "SYNC_ERROR"; error: string }
  | { type: "RETRY" };

export const iframeMachine = setup({
  types: {
    context: {} as IframeContext,
    events: {} as IframeEvent,
  },
  actions: {
    assignStoreData: assign(({ event }) => {
      if (event.type !== "STORE_INITIALIZED") return {};
      return {
        ehr: event.ehr,
        patient: event.patient,
        userEmail: event.userEmail,
        initialized: true,
      };
    }),
    markAuthenticated: assign({ authenticated: true }),
    setStitchPatient: assign(({ event }) => {
      if (event.type !== "SYNC_SUCCESS") return {};
      return { stitchPatient: event.patient };
    }),
    assignError: assign(({ event }) => {
      if (event.type !== "STORE_INITIALIZED") return {};
      return { error: "No EHR connector found" };
    }),
    clearError: assign({
      error: null,
      initialized: false,
      authenticated: false,
    }),
  },
  guards: {
    hasEhr: ({ context }) => context.ehr !== null,
    hasPatient: ({ context }) => context.patient !== null,
    isInitialized: ({ context }) =>
      context.initialized && context.authenticated,
  },
}).createMachine({
  id: "iframeLogin",
  initial: "initializing",
  context: {
    ehr: null,
    patient: null,
    userEmail: null,
    stitchPatient: null,
    error: null,
    initialized: false,
    authenticated: false,
  },

  states: {
    initializing: {
      on: {
        STORE_INITIALIZED: [
          {
            guard: ({ event }) => event.ehr === null,
            target: "error",
            actions: "assignError",
          },
          {
            actions: "assignStoreData",
          },
        ],
        SESSION_READY: {
          actions: "markAuthenticated",
        },
      },
      always: [
        {
          guard: ({ context }) =>
            context.initialized &&
            context.authenticated &&
            context.patient !== null,
          target: "syncing",
        },
        {
          guard: ({ context }) =>
            context.initialized &&
            context.authenticated &&
            context.patient === null,
          target: "redirectToPatients",
        },
      ],
    },

    syncing: {
      on: {
        SYNC_SUCCESS: {
          target: "redirectToBriefing",
          actions: "setStitchPatient",
        },
        SYNC_ERROR: {
          target: "redirectToPatients",
        },
      },
    },

    error: {
      on: {
        RETRY: {
          target: "initializing",
          actions: "clearError",
        },
      },
    },

    redirectToBriefing: { type: "final" },
    redirectToPatients: { type: "final" },
  },
});

const iframeActor: Actor<typeof iframeMachine> =
  createActor(iframeMachine).start();

export function sendIframeEvent(event: IframeEvent): void {
  iframeActor.send(event);
}

export function getIframeEhr(): Ehr | null {
  return iframeActor.getSnapshot().context.ehr;
}

export function usePatientFromIframe(): PatientFromIframe | null {
  return useSelector(iframeActor, (s) =>
    IS_IN_IFRAME ? s.context.patient : null,
  );
}

export function useEhrFromIframe(): Ehr | null {
  return useSelector(iframeActor, (s) => (IS_IN_IFRAME ? s.context.ehr : null));
}

export function useIsInElationEHR(): boolean {
  return useSelector(
    iframeActor,
    (s) => s.context.ehr === "elation" && IS_IN_IFRAME,
  );
}

export function useIframeLoginState() {
  return useSelector(iframeActor, (s) => s);
}

export function useIsIframeInitialized(): boolean {
  return useSelector(
    iframeActor,
    (s) => s.context.initialized && s.context.authenticated,
  );
}
